import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { AuthState } from "@frontegg/redux-store";
import * as Sentry from "@sentry/vue";
import { useToast } from "vue-toastification";
import { isValidUuid } from "@/services/Utils/uuid";

export const useAuthStateStore = defineStore("authStateStore", () => {
  const capacitId = import.meta.env.VITE_CAPACIT_ORG_ID;
  const gaiupId = import.meta.env.VITE_GAIUP_ORG_ID;
  const authState = ref<AuthState>();

  const setAuthState = (newAuthState: AuthState) => {
    if (newAuthState.user) {
      localStorage.setItem("carbon0.accessToken", newAuthState.user?.accessToken);
    }

    authState.value = newAuthState;
  };

  const hasAccessAllProjects = computed(() =>
    user.value ? !!user.value.roles.find((x) => x.name === "AccessAllProjects") : undefined
  );

  const isAdmin = computed(() => {
    if (!authState.value) return false;
    if (authState.value?.user?.tenantId === capacitId || authState.value?.user?.tenantId === gaiupId) return true;
    return false;
  });

  // Frontegg role for gaiup staff — gates purchase/invoices and the admin panel. Matched on key and
  // name, normalised, so a display name "Platform Admin" counts as the key "PlatformAdmin".
  const isPlatformAdmin = computed(() => {
    const normalise = (value: string | undefined) => value?.toLowerCase().replace(/[\s_-]/g, "") ?? "";

    return !!user.value?.roles?.some(
      (role) => normalise(role.key) === "platformadmin" || normalise(role.name) === "platformadmin"
    );
  });

  const isAuthenticated = computed(() => {
    return authState.value?.isAuthenticated || false;
  });

  const user = computed(() => {
    return authState.value?.user;
  });

  // Tenant-scoped Hasura queries filter on a uuid column and reject anything else with a GraphQL
  // error. Empty/undefined is the normal pre-login state and stays valid; per-query guards cover it.
  const isTenantIdValid = computed(() => !user.value?.tenantId || isValidUuid(user.value.tenantId));

  // Report once per malformed value, not once per consumer (recent projects, library
  // collections, …) and not on every auth-state tick for the same bad id.
  let lastReportedInvalidTenantId: string | undefined;
  watch(user, (newUser) => {
    const tenantId = newUser?.tenantId;
    if (tenantId && !isValidUuid(tenantId) && tenantId !== lastReportedInvalidTenantId) {
      lastReportedInvalidTenantId = tenantId;
      Sentry.captureException(new Error("Frontegg reported a non-UUID tenantId"), { tags: { tenantId } });
      useToast().error("Your account isn't linked to a valid tenant, so some data may not load. Please contact support.");
    }
  });

  return {
    authState,
    setAuthState,
    hasAccessAllProjects,
    isAdmin,
    isPlatformAdmin,
    isAuthenticated,
    isTenantIdValid,
    user,
  };
});
